import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
    Box,
    Typography,
    Grid,
    Divider,
} from '@mui/material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import ReportIcon from '@mui/icons-material/ReportGmailerrorred';
import { orderService } from '../../services/orderService';
import CommentIcon from '@mui/icons-material/ChatBubbleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

const getStatusColor = (status) => {
    switch (status) {
        case 'pending':
            return 'warning';
        case 'paid':
            return 'success';
        case 'cancelled':
            return 'error';
        default:
            return 'default';
    }
};

const getStatusText = (status) => {
    switch (status) {
        case 'pending':
            return 'Ожидает оплаты';
        case 'paid':
            return 'Оплачен';
        case 'cancelled':
            return 'Отменен';
        default:
            return status;
    }
};

const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'dd.MM.yyyy HH:mm');
};

const OrderBotSource = ({ source }) => {
    if (!source) return <Typography variant="body2" color="text.secondary">Не указан</Typography>;

    return (
        <Box>
            <Typography variant="body2">
                {source.username ? `@${source.username.replace(/^@+/, '')}` : 'Имя бота неизвестно'}
            </Typography>
            {source.unavailable && (
                <Typography variant="caption" color="text.secondary" display="block">
                    Бот удалён или недоступен
                </Typography>
            )}
            {source.usernameSource === 'current' && (
                <Typography variant="caption" color="text.secondary" display="block">
                    Текущее имя
                </Typography>
            )}
            {source.id && (
                <Typography variant="caption" color="text.secondary" display="block">
                    ID: {source.id}
                </Typography>
            )}
        </Box>
    );
};

const Row = ({ order }) => {
    const [open, setOpen] = useState(false);
    const [rowOrder, setRowOrder] = useState(order);
    const [nfOpen, setNfOpen] = useState(false);
    const [nfAmount, setNfAmount] = useState('');
    const [nfComment, setNfComment] = useState('');
    const [nfLoading, setNfLoading] = useState(false);
    const [commentOpen, setCommentOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const truncateOrderId = (id) => {
        if (!id) return '-';
        const s = String(id);
        if (s.length <= 14) return s;
        return `${s.slice(0, 8)}…${s.slice(-6)}`;
    };

    const copyOrderId = async () => {
        try {
            await navigator.clipboard.writeText(rowOrder.order_id);
            enqueueSnackbar('Скопировано', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Не удалось скопировать', { variant: 'error' });
        }
    };

    const copyPhotoLink = async () => {
        if (!rowOrder.address_id?.photo) return;
        try {
            await navigator.clipboard.writeText(rowOrder.address_id.photo);
            enqueueSnackbar('Ссылка на фото скопирована', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Не удалось скопировать ссылку', { variant: 'error' });
        }
    };

    const handleMarkNotFound = async () => {
        try {
            setNfLoading(true);
            await orderService.markNotFound(rowOrder.order_id, {
                topUpAmount: nfAmount ? Number(nfAmount) : 0,
                comment: nfComment
            });
            setRowOrder({ ...rowOrder, not_found: true, not_found_at: new Date().toISOString() });
            setNfOpen(false);
            setNfAmount('');
            setNfComment('');
        } catch (e) {
            console.error('Failed to mark not found', e);
        } finally {
            setNfLoading(false);
        }
    };

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Tooltip title="Нажмите, чтобы скопировать">
                        <Button size="small" onClick={copyOrderId} sx={{ fontFamily: 'monospace', textTransform: 'none', p: 0, minWidth: 0 }}>
                            {truncateOrderId(rowOrder.order_id)}
                        </Button>
                    </Tooltip>
                </TableCell>
                <TableCell>{rowOrder.user_id || '-'}</TableCell>
                <TableCell><OrderBotSource source={rowOrder.bot_source} /></TableCell>
                <TableCell align="right">
                    {rowOrder.status === 'paid' && !rowOrder.not_found && (
                        <Button size="small" color="warning" startIcon={<ReportIcon />} onClick={() => setNfOpen(true)}>
                            Ненаход
                        </Button>
                    )}
                    {rowOrder.not_found && rowOrder.not_found_comment && (
                        <Tooltip title="Комментарий ненахода">
                            <IconButton size="small" onClick={() => setCommentOpen(true)}>
                                <CommentIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={getStatusText(rowOrder.status)}
                        color={getStatusColor(rowOrder.status)}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    {rowOrder.address_id?.courier?.name} ({rowOrder.address_id?.courier?.username})
                </TableCell>
                <TableCell>
                    {rowOrder.type === 'purchase' ? 'Покупка' : rowOrder.type === 'buybalance' ? 'С баланса' : '-'}
                </TableCell>
                <TableCell>
                    {rowOrder.base_price.toFixed(2)} {rowOrder.cupon_price ? `+ Купон:${rowOrder.cupon_price.toFixed(2)}` : ''} ₽
                    <Tooltip title={`Итоговая цена: ${rowOrder.total_price.toFixed(2)} ₽\nКомиссия: ${(rowOrder.service_fee * 100 - 100).toFixed(0)}%`}>
                        <IconButton size="small">
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    {rowOrder.payment_method === 'sbp' ? 'СБП' :
                        rowOrder.payment_method === 'balance' ? 'Баланс' : '-'}
                </TableCell>
                <TableCell>{formatDate(rowOrder.created_at)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Детальная информация
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Информация о товаре:
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                        <Typography>Район: {rowOrder.address_id?.district?.name || '-'}</Typography>
                                        <Typography>Товар: {rowOrder.address_id?.product?.name || '-'}</Typography>
                                        <Typography>Фасовка: {rowOrder.address_id?.packaging?.name || '-'}</Typography>
                                        <Typography>Тип закладки: {rowOrder.address_id?.type || '-'}</Typography>
                                        <Typography>Дата заливки: {formatDate(rowOrder.address_id?.createdAt) || '-'}</Typography>
                                        {rowOrder.address_id?.photo && (
                                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    component="a"
                                                    href={rowOrder.address_id.photo}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ display: 'block', wordBreak: 'break-all' }}
                                                >
                                                    {rowOrder.address_id.photo}
                                                </Typography>
                                                <Tooltip title="Скопировать ссылку">
                                                    <IconButton size="small" onClick={copyPhotoLink}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Информация об оплате:
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                        <Typography>ID транзакции: {rowOrder.payment_details?.transaction_id || '-'}</Typography>
                                        <Typography>Время оплаты: {rowOrder.payment_details?.payment_time ? 
                                            formatDate(rowOrder.payment_details.payment_time) : '-'}</Typography>
                                        {rowOrder.payment_details?.payment_link && (
                                            <Typography>
                                                Ссылка на оплату: 
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => window.open(rowOrder.payment_details.payment_link, '_blank')}
                                                >
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" gutterBottom>
                                        Дополнительная информация:
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                        <Typography>Создан: {formatDate(rowOrder.created_at)}</Typography>
                                        <Typography>Обновлен: {formatDate(rowOrder.updated_at)}</Typography>
                                        <Typography>Истекает: {formatDate(rowOrder.expires_at)}</Typography>
                                        {rowOrder.address_id?.bots && (
                                            <Typography>Боты: {rowOrder.address_id.bots.join(', ')}</Typography>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {/* Диалог "Ненаход" */}
            <Dialog open={nfOpen} onClose={() => setNfOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Пометить как ненаход</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Заказ {rowOrder.order_id}. Вы можете опционально пополнить баланс пользователя.
                    </Typography>
                    <TextField
                        label="Сумма пополнения"
                        type="number"
                        fullWidth
                        value={nfAmount}
                        onChange={(e) => setNfAmount(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        label="Комментарий"
                        fullWidth
                        multiline
                        minRows={2}
                        value={nfComment}
                        onChange={(e) => setNfComment(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNfOpen(false)}>Отмена</Button>
                    <Button variant="contained" color="warning" onClick={handleMarkNotFound} disabled={nfLoading} startIcon={<ReportIcon />}>
                        Ненаход
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Диалог просмотра комментария */}
            <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Комментарий ненахода</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {rowOrder.not_found_comment || '—'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCommentOpen(false)}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

const OrderTable = ({ orders, total, page, limit, onPageChange, onLimitChange }) => {

    return (
        <Paper>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>ID заказа</TableCell>
                            <TableCell>User ID</TableCell>
                            <TableCell>Telegram-бот</TableCell>
                            <TableCell align="right">Действия</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Курьер</TableCell>
                            <TableCell>Тип</TableCell>
                            <TableCell>Сумма</TableCell>
                            <TableCell>Способ оплаты</TableCell>
                            <TableCell>Дата создания</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((order) => (
                            <Row key={order._id} order={order} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                page={page}
                onPageChange={(event, newPage) => onPageChange(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(event) => onLimitChange(parseInt(event.target.value, 10))}
                labelRowsPerPage="Строк на странице"
            />
        </Paper>
    );
};

export default OrderTable;
